import React, { useEffect, useState } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getCachedAvatar, getCachedDisplayName } from '../utils/helpers';
import { Search, Shield } from 'lucide-react';

const PAGE_SIZE = 20;

function StaffTab() {
  const { server } = useServer();
  const [staff, setStaff] = useState([]);
  const [ratings, setRatings] = useState({});
  const [avatars, setAvatars] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [names, setNames] = useState({});
  const [search, setSearch] = useState('');
  const [rankFilter, setRankFilter] = useState('all');
  const [onlineStats, setOnlineStats] = useState({});
  const [onlineStatsLoading, setOnlineStatsLoading] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchStaff();
  }, [server]);

  useEffect(() => {
    setPage(1);
  }, [search, rankFilter]);

  useEffect(() => {
    const pageStaff = getFilteredStaff();
    pageStaff.forEach(s => {
      if (!avatars[s.steamid] && s.steamid) {
        const sid64 = s.steamid.startsWith('STEAM_')
          ? (() => { const parts = s.steamid.split(':'); const Y = parseInt(parts[1], 10); const Z = parseInt(parts[2], 10); return (window.BigInt(Z) * 2n + window.BigInt(Y) + 76561197960265728n).toString(); })()
          : s.steamid;
        getCachedAvatar(sid64, async () => {
          const resp = await api.get(`/api/players/avatar/${sid64}`);
          return resp.data.avatar;
        }).then(url => {
          setAvatars(prev => ({ ...prev, [s.steamid]: url }));
        }).catch(() => {
          setAvatars(prev => ({ ...prev, [s.steamid]: null }));
        });
      }
      if (!names[s.steamid] && s.steamid) {
        const sid64 = s.steamid.startsWith('STEAM_')
          ? (() => { const parts = s.steamid.split(':'); const Y = parseInt(parts[1], 10); const Z = parseInt(parts[2], 10); return (window.BigInt(Z) * 2n + window.BigInt(Y) + 76561197960265728n).toString(); })()
          : s.steamid;
        getCachedDisplayName(sid64, async () => {
          const resp = await api.get(`/api/players/${s.steamid}`);
          return resp.data.displayName || resp.data.name || s.steamid;
        }).then(name => {
          setNames(prev => ({ ...prev, [s.steamid]: name }));
        }).catch(() => {
          setNames(prev => ({ ...prev, [s.steamid]: s.steamid }));
        });
      }
      
      // Получаем онлайн статистику
      if (s.steamid) {
        fetchOnlineStats(s.steamid);
      }
    });
  }, [staff, page, server]);

  const fetchOnlineStats = async (steamId) => {
    if (onlineStats[steamId] || onlineStatsLoading[steamId]) return;
    
    setOnlineStatsLoading(prev => ({ ...prev, [steamId]: true }));
    
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      
      const res = await api.get(`/api/players/${steamId}/online-stats?server=${server}`, {
        params: {
          year: currentYear,
          month: currentMonth
        }
      });
      
      if (res.data && res.data.days) {
        const today = now.getDate();
        let totalMinutes = 0;
        
        // Считаем за последние 7 дней
        for (let i = 0; i < 7; i++) {
          const dayIdx = today - 1 - i;
          if (dayIdx >= 0 && Array.isArray(res.data.days[dayIdx])) {
            totalMinutes += res.data.days[dayIdx].reduce((a, b) => a + (b || 0), 0);
          }
        }
        
        const hours = (totalMinutes / 60).toFixed(1);
        setOnlineStats(prev => ({ ...prev, [steamId]: hours }));
      }
    } catch (error) {
      console.error('Ошибка при получении онлайн статистики для', steamId, error);
      setOnlineStats(prev => ({ ...prev, [steamId]: '0.0' }));
    } finally {
      setOnlineStatsLoading(prev => ({ ...prev, [steamId]: false }));
    }
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/staff`);
      let staffList = res.data.staff || [];
      staffList = staffList.filter(s => s.rank !== 'yufuadmin' && s.rank !== 'superadmin');
      const ids = staffList.map(s => (s.steamid.startsWith('STEAM_')
        ? (() => { const parts = s.steamid.split(':'); const Y = parseInt(parts[1], 10); const Z = parseInt(parts[2], 10); return (window.BigInt(Z) * 2n + window.BigInt(Y) + 76561197960265728n).toString(); })()
        : s.steamid));
      let ratingsObj = {};
      if (ids.length > 0) {
        try {
          const resp = await api.get(`/api/admin/ratings?steamids=${ids.join(',')}`);
          ratingsObj = resp.data;
        } catch { ratingsObj = {}; }
      }
      setRatings(ratingsObj);
      staffList.sort((a, b) => {
        const sid64a = a.steamid.startsWith('STEAM_')
          ? (() => { const parts = a.steamid.split(':'); const Y = parseInt(parts[1], 10); const Z = parseInt(parts[2], 10); return (window.BigInt(Z) * 2n + window.BigInt(Y) + 76561197960265728n).toString(); })()
          : a.steamid;
        const sid64b = b.steamid.startsWith('STEAM_')
          ? (() => { const parts = b.steamid.split(':'); const Y = parseInt(parts[1], 10); const Z = parseInt(parts[2], 10); return (window.BigInt(Z) * 2n + window.BigInt(Y) + 76561197960265728n).toString(); })()
          : b.steamid;
        const ra = ratingsObj[sid64a] || { total: 0, num: 0 };
        const rb = ratingsObj[sid64b] || { total: 0, num: 0 };
        const avga = ra.num > 0 ? ra.total / ra.num : 0;
        const avgb = rb.num > 0 ? rb.total / rb.num : 0;
        return avgb - avga;
      });
      setStaff(staffList);
      setAvatars({});
      setNames({});
      setPage(1);
    } catch (e) {
      toast.error('Ошибка при загрузке staff');
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  function getFilteredStaff() {
    let filtered = staff;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      filtered = filtered.filter(st =>
        (names[st.steamid] || st.steamid || '').toLowerCase().includes(s) ||
        (st.steamid || '').toLowerCase().includes(s)
      );
    }
    if (rankFilter !== 'all') {
      filtered = filtered.filter(st => st.rank === rankFilter);
    }
    return filtered;
  }

  function getPageStaff() {
    const filtered = getFilteredStaff();
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }

  const totalPages = Math.ceil(getFilteredStaff().length / PAGE_SIZE);

  const allRanks = Array.from(new Set(staff.map(s => s.rank))).sort();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gmod-white ml-4">Стафф</h1>
        <p className="text-gmod-white opacity-60 mt-2 ml-4">Список стаффа сервера</p>
      </div>
      <div className="bg-gmod-dark rounded-lg p-6 border border-gmod-lighterer">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gmod-white opacity-60" />
              <input
                type="text"
                placeholder="Поиск по имени или Steam ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white placeholder-gmod-white placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-theme-primary"
              />
            </div>
          </div>
          <div>
            <select
              value={rankFilter}
              onChange={e => setRankFilter(e.target.value)}
              className="px-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
            >
              <option value="all">Все ранги</option>
              {allRanks.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
          </div>
        ) : getFilteredStaff().length === 0 ? (
          <p className="text-gmod-white opacity-60 text-center py-8">Стафф не найден</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getPageStaff().map(s => {
              const sid64 = s.steamid && (s.steamid.startsWith('STEAM_')
                ? (() => { const parts = s.steamid.split(':'); const Y = parseInt(parts[1], 10); const Z = parseInt(parts[2], 10); return (window.BigInt(Z) * 2n + window.BigInt(Y) + 76561197960265728n).toString(); })()
                : s.steamid);
              const rating = ratings[sid64] || { total: 0, num: 0 };
              const avg = rating.num > 0 ? rating.total / rating.num : 0;
              return (
                <div
                  key={s.steamid}
                  className="flex items-center bg-gmod-lighter rounded-lg p-4 border border-gmod-lighterer hover:bg-gmod-dark transition-colors cursor-pointer gap-4"
                  onClick={() => navigate(`/players/${s.steamid}`)}
                >
                  <span className="flex items-center min-w-[48px] w-12 h-12 justify-center">
                    {avatars[s.steamid] ? (
                      <img src={avatars[s.steamid]} alt="avatar" className="w-12 h-12 min-w-[48px] rounded-full border-2 border-theme-primary bg-gmod-dark object-cover" />
                    ) : (
                      <Shield className="w-12 h-12 min-w-[48px] text-gmod-white opacity-40" />
                    )}
                  </span>
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gmod-white font-medium truncate max-w-[180px]">{names[s.steamid] || s.steamid}</span>
                      <span className="text-xs bg-theme-primary text-gmod-white rounded px-2 py-0.5 ml-2">{s.rank}</span>
                    </div>
                    <span className="text-gmod-white opacity-60 text-xs font-mono truncate">{s.steamid}</span>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gmod-white opacity-40 mt-1">
                      <span>Иммунитет: {s.immunity}</span>
                      <span>Жалоб: {rating.num}</span>
                      <span>Рейтинг: {rating.num > 0 ? avg.toFixed(2) : '-'}</span>
                      {onlineStatsLoading[s.steamid] ? (
                        <span className="text-theme-primary">Загрузка...</span>
                      ) : (
                        <span>Неделя: {onlineStats[s.steamid] || '0.0'}ч</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex justify-center space-x-2 mt-4">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Назад</button>
            <span className="px-4 py-2 text-gmod-white">{page} из {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Вперёд</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffTab; 